import React, { useMemo } from 'react';
import { Card, CardContent } from '@shared/components/ui/card';
import { cn } from '@shared/lib/utils';
import { PANEL_TEXT_STYLES } from '../../constants/text-styles';
import type { Terminal } from '@features/in-stores/types/terminal.types';
import type { PaymentDetail } from '../../types/panel-common.types';

interface TerminalSummaryCardProps {
  terminal?: Terminal | null;
  className?: string;
}

export const TerminalSummaryCard = React.memo<TerminalSummaryCardProps>(({
  terminal,
  className,
}) => {
  const details = useMemo<PaymentDetail[]>(() => {
    if (!terminal) return [];
    const location = typeof terminal.location === 'string'
      ? terminal.location
      : [terminal.location?.city, terminal.location?.state].filter(Boolean).join(', ') || 'Unknown';

    return [
      { label: 'TID', value: terminal.tid },
      { label: 'Model', value: terminal.model },
      { label: 'Type', value: terminal.type },
      { label: 'Store', value: terminal.store },
      { label: 'Location', value: location },
    ];
  }, [terminal]);

  if (!terminal) {
    return (
      <Card className={cn('mb-0', className)}>
        <CardContent className="px-5 pt-4 pb-5">
          <p className="text-sm text-muted-foreground">No terminal data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('mb-0', className)}>
      <CardContent className="px-5 pt-4 pb-5">
        <div className="flex flex-col gap-4">
          {details.map((detail) => (
            <div key={detail.label} className="flex flex-col gap-1">
              <p className={PANEL_TEXT_STYLES.label}>{detail.label}</p>
              <div className={PANEL_TEXT_STYLES.value}>
                <span>{detail.value}</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
});

TerminalSummaryCard.displayName = 'TerminalSummaryCard';
